import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDataCache, useCache } from '../contexts/DataCacheContext';
import { getAthleteWorkoutLogs, getAthleteWeeklyVolumeDetailed, deleteWorkoutLog } from '../lib/database';
import { useSwipeBack } from '../hooks/useSwipeBack';
import SkeletonLoader from './SkeletonLoader';
import './AthleteHistory.css';

function AthleteHistory() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const toast = useToast();
  const { invalidate: invalidateCache } = useCache();

  // Weekly volume state
  const [weekOffset, setWeekOffset] = useState(0);
  const [expandedMuscle, setExpandedMuscle] = useState(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  const { handleTouchStart, handleTouchEnd } = useSwipeBack(() => navigate('/athlete'));

  const getWeekStart = useCallback((offset) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff + (offset * 7));
    return d;
  }, []);

  // Cached workout logs
  const fetchLogs = useCallback(
    () => getAthleteWorkoutLogs(profile?.id, 20),
    [profile?.id]
  );
  const { data: workoutLogs, loading } = useDataCache(
    profile?.id ? `athlete-workout-logs-${profile.id}` : null,
    fetchLogs,
    60000
  );

  // Cached volume data (re-keyed per weekOffset)
  const fetchVolume = useCallback(
    () => getAthleteWeeklyVolumeDetailed(profile?.id, getWeekStart(weekOffset)),
    [profile?.id, weekOffset, getWeekStart]
  );
  const { data: volumeData, loading: volumeLoading } = useDataCache(
    profile?.id ? `athlete-volume-${profile.id}-w${weekOffset}` : null,
    fetchVolume,
    60000
  );

  const handleBack = () => navigate('/athlete');
  const handleWorkoutClick = (workoutLogId) => navigate(`/athlete/history/${workoutLogId}`);

  const handleDeleteClick = (e, log) => {
    e.stopPropagation(); // Prevent navigation to detail
    const workoutName = log.workout?.name || 'Allenamento';
    setDeleteConfirm({ id: log.id, name: workoutName });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm || deleting) return;

    setDeleting(true);
    try {
      await deleteWorkoutLog(deleteConfirm.id, profile.id);

      // Invalidate caches to refresh data
      invalidateCache(`athlete-workout-logs-${profile.id}`);
      invalidateCache(`athlete-volume-${profile.id}-w${weekOffset}`);
      invalidateCache(`athlete-dashboard-${profile.id}`);

      setDeleteConfirm(null);
      toast.success('Allenamento cancellato');

      // Force page reload to refresh data
      window.location.reload();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Errore durante la cancellazione: ' + error.message);
    } finally{
      setDeleting(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    }).format(date);
  };

  const formatWeekRange = () => {
    if (!volumeData?.weekStart) return '';
    const start = new Date(volumeData.weekStart);
    const end = new Date(volumeData.weekEnd);
    end.setDate(end.getDate() - 1); // End is exclusive, show last day
    const fmt = (d) => new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' }).format(d);
    return `${fmt(start)} – ${fmt(end)}`;
  };

  const isCurrentWeek = weekOffset === 0;

  const muscleColors = {
    'Petto': '#ef4444',
    'Schiena': '#3b82f6',
    'Spalle': '#f59e0b',
    'Bicipiti': '#10b981',
    'Tricipiti': '#8b5cf6',
    'Quadricipiti': '#ec4899',
    'Femorali': '#06b6d4',
    'Glutei': '#f97316',
    'Polpacci': '#84cc16',
    'Addominali': '#6366f1',
    'Trapezio': '#14b8a6',
    'Avambracci': '#a855f7',
    'Core': '#6366f1',
    'Altro': '#9ca3af',
  };

  const getMuscleColor = (muscle) => muscleColors[muscle] || '#6b7280';

  const sortedMuscles = volumeData?.muscles
    ? Object.entries(volumeData.muscles).sort((a, b) => b[1].total - a[1].total)
    : [];

  return (
    <div className="ah-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {/* Sticky Header */}
      <header className="ah-header">
        <button className="ah-header-back" onClick={handleBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <span className="ah-header-title">Storico Allenamenti</span>
      </header>

      <main className="ah-main">
        {/* Weekly Volume Section */}
        <section className="ah-volume-section">
          <div className="ah-volume-header">
            <h2 className="ah-volume-title">Serie Settimanali</h2>
            <div className="ah-week-nav">
              <button className="ah-week-btn" onClick={() => setWeekOffset(prev => prev - 1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <span className="ah-week-label">
                {volumeLoading ? '...' : formatWeekRange()}
                {isCurrentWeek && <span className="ah-week-badge">Questa sett.</span>}
              </span>
              <button
                className="ah-week-btn"
                onClick={() => setWeekOffset(prev => Math.min(prev + 1, 0))}
                disabled={isCurrentWeek}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          </div>

          {volumeLoading ? (
            <div className="ah-volume-skeleton">
              <SkeletonLoader variant="volume" />
            </div>
          ) : sortedMuscles.length > 0 ? (
            <div className="ah-volume-list">
              {sortedMuscles.map(([muscle, data]) => {
                const isExpanded = expandedMuscle === muscle;
                const exerciseEntries = Object.entries(data.exercises).sort((a, b) => b[1] - a[1]);
                return (
                  <div key={muscle} className="ah-muscle-item">
                    <button
                      className="ah-muscle-row"
                      onClick={() => setExpandedMuscle(isExpanded ? null : muscle)}
                    >
                      <div className="ah-muscle-bar" style={{ backgroundColor: getMuscleColor(muscle) }} />
                      <span className="ah-muscle-name">{muscle}</span>
                      <span className="ah-muscle-total">{data.total % 1 === 0 ? data.total : data.total.toFixed(1)} serie</span>
                      <svg
                        className={`ah-muscle-chevron ${isExpanded ? 'ah-muscle-chevron--open' : ''}`}
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </button>
                    {isExpanded && exerciseEntries.length > 0 && (
                      <div className="ah-exercise-breakdown">
                        {exerciseEntries.map(([name, sets]) => (
                          <div key={name} className="ah-exercise-row">
                            <span className="ah-exercise-name">{name}</span>
                            <span className="ah-exercise-sets">{sets % 1 === 0 ? sets : sets.toFixed(1)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="ah-volume-empty">Nessun allenamento in questa settimana</p>
          )}
        </section>

        {/* Workout Log List */}
        <section className="ah-logs-section">
          <h2 className="ah-section-title">Allenamenti recenti</h2>

          {loading ? (
            <SkeletonLoader variant="list" count={3} />
          ) : (!workoutLogs || workoutLogs.length === 0) ? (
            <div className="ah-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <p className="ah-empty-title">Nessun allenamento completato</p>
              <p className="ah-empty-sub">Completa il tuo primo allenamento per vederlo qui!</p>
            </div>
          ) : (
            <div className="ah-log-list">
              {workoutLogs.map(log => (
                <div
                  key={log.id}
                  className="ah-log-card"
                  onClick={() => handleWorkoutClick(log.id)}
                >
                  <div className="ah-log-top">
                    <span className="ah-log-date">{formatDate(log.completed_at)}</span>
                    <div className="ah-log-top-right">
                      {log.duration_minutes && (
                        <span className="ah-log-duration">{log.duration_minutes} min</span>
                      )}
                      <button
                        className="ah-log-delete"
                        onClick={(e) => handleDeleteClick(e, log)}
                        title="Elimina allenamento"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          <line x1="10" y1="11" x2="10" y2="17"/>
                          <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="ah-log-body">
                    {log.workout ? (
                      <>
                        {log.workout.program && (
                          <span className="ah-log-program">{log.workout.program.name}</span>
                        )}
                        <span className="ah-log-name">{log.workout.name}</span>
                      </>
                    ) : (
                      <span className="ah-log-name ah-log-name--deleted">Workout eliminato</span>
                    )}
                  </div>
                  <div className="ah-log-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="ah-modal-overlay" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="ah-modal" onClick={e => e.stopPropagation()}>
            <div className="ah-modal-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </div>
            <h3 className="ah-modal-title">Eliminare allenamento?</h3>
            <p className="ah-modal-text">
              Stai per eliminare <strong>{deleteConfirm.name}</strong> e tutti i dati associati.
              Questa azione non può essere annullata.
            </p>
            <div className="ah-modal-buttons">
              <button
                className="ah-modal-btn ah-modal-btn--cancel"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Annulla
              </button>
              <button
                className="ah-modal-btn ah-modal-btn--delete"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Eliminazione...' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AthleteHistory;
